import { Injectable } from '@angular/core';
import _ from 'lodash';
import {KnockoutFieldModel} from '../../components/knockout-field/knockout-field.model';

declare type FieldServiceCustomType = {
  onChange?: any;
}

declare type FieldServiceOptionsType = KnockoutFieldModel & FieldServiceCustomType;

@Injectable({
  providedIn: 'root'
})
export class FieldService {
  constructor() {
    (window as any).NG_SHARED_FIELD_SERVICE = this;
  }

  createNewField(name: string, dataType: string, renderer: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    if (options === undefined) {
      options = {};
    }

    let defaultOnChangeFunc = (value: any) => {

    };

    if (options.onChange !== undefined) {
        defaultOnChangeFunc = options.onChange;
    }

    _.defaults(
      options,
      {
        name: name,
        i18nKey: i18nLabelKey,
        datatype: dataType,
        availableValues: [],
        referenceTable: null,
        readonly: false,
        mandatory: false,
        enablePreview: false,
        tooltip: null,
        data: {
          defaultValue: null,
          defaultValueSource: 'script',
          answer: {
            value: null,
            displayValue: null
          },
        },
        requestId: null,
        table: 'SyntheticTable',
        renderer: renderer,
        rendererProperties: {},
        valueChangeCallback: (value: any) => {
            if (value !== undefined) {
                defaultOnChangeFunc(value);
            }
        },
        keyupEventCallback: (fieldViewModel: any, event: any) => {
            defaultOnChangeFunc(event.target.value);
        }
      }
    );

    return options;
  }

  public createChoiceField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "choice", "choice", i18nLabelKey, options);
  }

  public createTextField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "text", "text", i18nLabelKey, options);
  }

  public createTextAreaField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "text", "textarea", i18nLabelKey, options);
  }

  public createHtmlField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "html", "html", i18nLabelKey, options);
  }

  public createBooleanSwitchField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "boolean", "switch", i18nLabelKey, options);
  }

  public createBooleanCheckboxField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "boolean", "checkbox", i18nLabelKey, options);
  }

  public createAcknowledgementField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "acknowledgement", "checkbox", i18nLabelKey, options);
  }

  public createIntegerField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "integer", "textfield", i18nLabelKey, options);
  }

  public createStringField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "string", "textfield", i18nLabelKey, options);
  }

  public createDateTimeField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "date_time", "datetime", i18nLabelKey, options);
  }

  public createDateField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "date", "date", i18nLabelKey, options);
  }

  public createDecimalField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "decimal", "textfield", i18nLabelKey, options);
  }

  public createDurationField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    return this.createNewField(name, "duration", "duration", i18nLabelKey, options);
  }

  /**
   * Create a reference field model. The field name must be the same as the referenceTable.
   */
  public createReferenceField(name: string, i18nLabelKey: string, referenceTable: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    _.defaults(
      options,
      {

      }
    );

    return this.createNewField(
      name,
      "reference",
      "onetoone",
      i18nLabelKey,
      options
    );
  }

  /**
   * Create a multi-reference field model. The field name must be the same as the referenceTable.
   */
  public createMultiReferenceField(name: string, i18nLabelKey: string, referenceTable: string, options?: FieldServiceOptionsType): KnockoutFieldModel {
    _.defaults(
      options,
      {

      }
    );

    return this.createNewField(
      name,
      "many_to_many",
      "onetomany",
      i18nLabelKey,
      options
    );
  }
}

//
// Because of the way that FieldService is injected into the context for dynamic components in DynamicComponentService,
// we cannot use the type definition for the class for intellisense. Instead, we create another one for the Angular component editor.
// It has to match the functions declared in the class.
//

/**
 * Helper functions to create the data structure for use with the KnockoutFieldComponent Angular component (sb-knockout-field)
 */
export declare namespace FieldService {
  function createNewField(name: string, dataType: string, renderer: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createChoiceField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createTextField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createTextAreaField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createHtmlField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createBooleanSwitchField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createBooleanCheckboxField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createAcknowledgementField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createIntegerField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createStringField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createDateTimeField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createDateField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createDecimalField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  function createDurationField(name: string, i18nLabelKey: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  /**
   * Create a reference field model. The field name must be the same as the referenceTable.
   */
  function  createReferenceField(name: string, i18nLabelKey: string, referenceTable: string, options?: FieldServiceOptionsType): KnockoutFieldModel;

  /**
   * Create a multi-reference field model. The field name must be the same as the referenceTable.
   */
  function  createMultiReferenceField(name: string, i18nLabelKey: string, referenceTable: string, options?: FieldServiceOptionsType): KnockoutFieldModel;
}
